import React from 'react';
import Badge from '@/components/common/Badge';
import { formatCurrency } from '@/utils/formatters';

/**
 * Componente de resumen de inventario
 * Muestra el total de repuestos, stock, valor de inventario y márgenes
 * @param {Object} props
 * @param {Array} props.repuestos - Lista de repuestos a resumir
 */
const ResumenInventario = ({ repuestos = [] }) => {
  const totalRepuestos = repuestos.length;
  const totalStock = repuestos.reduce((acc, r) => {
    const stock = r.Stock || r.StockSucursal || 0;
    return acc + (typeof stock === 'number' ? stock : 0);
  }, 0);
  
  const totalBajo = repuestos.filter(r => {
    const stock = r.Stock || r.StockSucursal || 0;
    const stockMinimo = r.StockMinimo || r.StockMinimoSucursal || 0;
    return stock != null && stockMinimo != null && stock <= stockMinimo;
  }).length;
  
  // Cálculos de valor de inventario
  const valorInventarioCompra = repuestos.reduce((acc, r) => {
    const stock = r.Stock || r.StockSucursal || 0;
    const precioCompra = r.PrecioCompra || 0;
    return acc + (stock * precioCompra);
  }, 0);
  
  const valorInventarioVenta = repuestos.reduce((acc, r) => {
    const stock = r.Stock || r.StockSucursal || 0;
    const precioVenta = r.PrecioVenta || r.Precio || r.PrecioSucursal || 0;
    return acc + (stock * precioVenta);
  }, 0);
  
  // Margen promedio ponderado
  const margenTotal = valorInventarioVenta - valorInventarioCompra;
  const margenPorcentaje = valorInventarioCompra > 0 ? (margenTotal / valorInventarioCompra) * 100 : 0;

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
      <div className="bg-gray-800 rounded-lg p-4 text-center border border-gray-700">
        <div className="text-xs text-gray-400 mb-1">Repuestos</div>
        <div className="text-2xl font-bold text-blue-400">{totalRepuestos}</div>
      </div>
      
      <div className="bg-gray-800 rounded-lg p-4 text-center border border-gray-700">
        <div className="text-xs text-gray-400 mb-1">Stock total</div>
        <div className="text-2xl font-bold text-green-400">{totalStock}</div>
      </div>
      
      <div className="bg-gray-800 rounded-lg p-4 text-center border border-gray-700">
        <div className="text-xs text-gray-400 mb-1">Stock bajo</div>
        <div className="text-2xl font-bold text-yellow-400 flex items-center justify-center gap-1">
          {totalBajo}
          {totalBajo > 0 && <Badge variant="warning" size="xs">¡Atención!</Badge>}
        </div>
      </div>
      
      <div className="bg-gray-800 rounded-lg p-4 text-center border border-gray-700">
        <div className="text-xs text-gray-400 mb-1">Valor inventario</div>
        <div className="text-lg font-bold text-purple-400">
          {formatCurrency(valorInventarioVenta)}
        </div>
        <div className="text-xs text-gray-500">
          Costo: {formatCurrency(valorInventarioCompra)}
        </div>
      </div>
      
      <div className="bg-gray-800 rounded-lg p-4 text-center border border-gray-700">
        <div className="text-xs text-gray-400 mb-1">Margen promedio</div>
        <div className={`text-2xl font-bold ${margenPorcentaje >= 50 ? 'text-green-400' : margenPorcentaje >= 30 ? 'text-yellow-400' : 'text-red-400'}`}>
          {margenPorcentaje.toFixed(0)}%
        </div>
        <div className="text-xs text-gray-500">
          +{formatCurrency(margenTotal)}
        </div>
      </div>
    </div>
  );
};

export default ResumenInventario;
